import {
  ChartColumn,
  ChevronLeft,
  ChevronRight,
  Circle,
  Frame,
  Hexagon,
  LayoutGrid,
  Minus,
  MoveUpRight,
  QrCode,
  Search,
  Smartphone,
  Smile,
  Square,
  SquareDashed,
  Star,
  Table2,
  Triangle,
  Wallpaper,
  X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { cn } from "../lib/utils";
import { BackgroundsInsert } from "./Backgrounds";
import { ChartInsert } from "./ChartProperties";
import { CollageInsert } from "./CollageInsert";
import { addShape, addText } from "./commands";
import { LinesAndDrawing } from "./DrawTool";
import { FramesInsert } from "./FramesInsert";
import { IconInsert, IconSearchPreview } from "./IconLibrary";
import { useLeftNav } from "./left-nav";
import { MockupsPanel } from "./Mockups";
import { PanelTitle } from "./PanelTitle";
import { QrInsert } from "./QrProperties";
import { useEditor, useEditorApi } from "./store";
import { TableInsert } from "./TableProperties";

/**
 * „Elemente“: text and shapes at hand, everything else behind a category
 * card (one list with ten sections was hard to scan), plus a search across all.
 */

interface Category {
  id: string;
  label: string;
  hint: string;
  icon: ReactNode;
  /** Tint of the card icon. */
  tone: string;
  keywords: string[];
  designOnly?: boolean;
  /** Brings its own scrolling layout: shown only when opened, not in search results. */
  fullPanel?: boolean;
  render: (query: string) => ReactNode;
}

const CATEGORIES: Category[] = [
  {
    id: "mockups",
    label: "Mockups",
    hint: "Screenshot im Gerät",
    icon: <Smartphone />,
    tone: "bg-indigo-100 text-indigo-700",
    keywords: [
      "mockup",
      "gerät",
      "handy",
      "smartphone",
      "iphone",
      "laptop",
      "tablet",
      "browser",
      "screenshot",
      "app",
    ],
    fullPanel: true,
    render: () => <MockupsPanel bare />,
  },
  {
    id: "lines",
    label: "Linien & Pfeile",
    hint: "Auch freies Zeichnen",
    icon: <MoveUpRight />,
    tone: "bg-sky-100 text-sky-700",
    keywords: ["linie", "pfeil", "strich", "verbinder", "zeichnen", "stift", "freihand", "marker"],
    render: () => <LinesAndDrawing />,
  },
  {
    id: "icons",
    label: "Icons",
    hint: "Symbole suchen",
    icon: <Smile />,
    tone: "bg-amber-100 text-amber-700",
    keywords: ["icon", "symbol", "piktogramm", "emoji"],
    render: (query) => <IconInsert query={query} />,
  },
  {
    id: "charts",
    label: "Diagramme",
    hint: "Balken, Linie, Kreis …",
    icon: <ChartColumn />,
    tone: "bg-emerald-100 text-emerald-700",
    keywords: [
      "diagramm",
      "chart",
      "balken",
      "säule",
      "torte",
      "kreisdiagramm",
      "statistik",
      "daten",
      "zahlen",
    ],
    render: () => <ChartInsert />,
  },
  {
    id: "tables",
    label: "Tabellen",
    hint: "Zeilen & Spalten",
    icon: <Table2 />,
    tone: "bg-violet-100 text-violet-700",
    keywords: ["tabelle", "zeile", "spalte", "preisliste", "liste"],
    render: () => <TableInsert />,
  },
  {
    id: "frames",
    label: "Bildrahmen",
    hint: "Bild in einer Form",
    icon: <Frame />,
    tone: "bg-rose-100 text-rose-700",
    keywords: ["rahmen", "bildrahmen", "maske", "platzhalter", "foto", "bild"],
    render: () => <FramesInsert />,
  },
  {
    id: "collage",
    label: "Collagen",
    hint: "Raster für Bilder",
    icon: <LayoutGrid />,
    tone: "bg-orange-100 text-orange-700",
    keywords: ["collage", "raster", "grid", "bilder", "fotos"],
    designOnly: true,
    render: () => <CollageInsert />,
  },
  {
    id: "qr",
    label: "QR-Code",
    hint: "Link als Code",
    icon: <QrCode />,
    tone: "bg-zinc-200 text-zinc-700",
    keywords: ["qr", "code", "link", "url", "barcode", "webseite"],
    render: () => <QrInsert />,
  },
  {
    id: "backgrounds",
    label: "Hintergründe",
    hint: "Muster, Verläufe, Deko",
    icon: <Wallpaper />,
    tone: "bg-teal-100 text-teal-700",
    keywords: ["hintergrund", "muster", "verlauf", "farbverlauf", "deko", "textur"],
    render: () => <BackgroundsInsert />,
  },
];

const TEXT_KEYWORDS = [
  "text",
  "überschrift",
  "titel",
  "schrift",
  "fließtext",
  "absatz",
  "headline",
];
const SHAPE_KEYWORDS = [
  "form",
  "rechteck",
  "quadrat",
  "kreis",
  "ellipse",
  "dreieck",
  "stern",
  "vieleck",
  "polygon",
  "sechseck",
  "linie",
];

/** Lower case without accents: „fliesstext“ and „Überschrift“ still match. */
function normalize(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .trim();
}

function matches(keywords: string[], label: string, query: string) {
  const q = normalize(query);
  if (q.length < 2) return false;
  return [label, ...keywords].some((keyword) => {
    const k = normalize(keyword);
    return k.includes(q) || (q.length >= 4 && q.includes(k));
  });
}

function SectionLabel({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold text-zinc-700">{children}</p>
      {action}
    </div>
  );
}

function Tile({
  onClick,
  children,
  title,
}: {
  onClick: () => void;
  children: ReactNode;
  title: string;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className="flex aspect-square items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 text-zinc-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 [&_svg]:size-6"
    >
      {children}
    </button>
  );
}

function TextButtons() {
  const store = useEditorApi();
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => addText(store, "heading")}
        className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-left text-lg font-extrabold hover:border-brand-300 hover:bg-brand-50"
      >
        Überschrift
      </button>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => addText(store, "subheading")}
          className="rounded-lg border border-zinc-200 px-3 py-2 text-left text-sm font-semibold hover:border-brand-300 hover:bg-brand-50"
        >
          Unterzeile
        </button>
        <button
          type="button"
          onClick={() => addText(store, "body")}
          className="rounded-lg border border-zinc-200 px-3 py-2 text-left text-sm hover:border-brand-300 hover:bg-brand-50"
        >
          Fließtext
        </button>
      </div>
    </div>
  );
}

function ShapeTiles() {
  const store = useEditorApi();
  return (
    <div className="grid grid-cols-4 gap-2">
      <Tile title="Rechteck (R)" onClick={() => addShape(store, "rect")}>
        <Square />
      </Tile>
      <Tile title="Abgerundetes Rechteck" onClick={() => addShape(store, "rect", "rounded")}>
        <SquareDashed className="rounded-lg" />
      </Tile>
      <Tile title="Ellipse (O)" onClick={() => addShape(store, "ellipse")}>
        <Circle />
      </Tile>
      <Tile title="Dreieck" onClick={() => addShape(store, "triangle")}>
        <Triangle />
      </Tile>
      <Tile title="Vieleck" onClick={() => addShape(store, "polygon")}>
        <Hexagon />
      </Tile>
      <Tile title="Stern" onClick={() => addShape(store, "star")}>
        <Star />
      </Tile>
      <Tile title="Linie (L)" onClick={() => addShape(store, "line")}>
        <Minus />
      </Tile>
      <Tile title="Gestrichelte Linie" onClick={() => addShape(store, "line", "dashed")}>
        <span className="text-xl leading-none tracking-widest">- -</span>
      </Tile>
    </div>
  );
}

function CategoryCard({ category, onOpen }: { category: Category; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col items-start gap-1.5 rounded-lg border border-zinc-200 p-2.5 text-left transition hover:border-brand-300 hover:bg-brand-50"
    >
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-4",
          category.tone,
        )}
      >
        {category.icon}
      </span>
      <span className="w-full min-w-0">
        <span className="block truncate text-xs font-medium text-zinc-900">{category.label}</span>
        <span className="block truncate text-[10px] text-zinc-500">{category.hint}</span>
      </span>
    </button>
  );
}

export function ElementsPanel() {
  const readOnly = useEditor((s) => s.readOnly);
  const isVideo = useEditor((s) => s.doc.kind === "video");
  const openId = useLeftNav((s) => s.elementsCategory);
  const setOpenId = useLeftNav((s) => s.setElementsCategory);
  const [query, setQuery] = useState("");
  /** Search carried into the icon category („Alle Icons“ from the results). */
  const [iconQuery, setIconQuery] = useState("");
  const categories = CATEGORIES.filter((category) => !(isVideo && category.designOnly));
  const open = categories.find((category) => category.id === openId);

  if (readOnly)
    return (
      <p className="p-4 text-sm text-zinc-500">
        Im Ansichtsmodus lassen sich keine Elemente hinzufügen.
      </p>
    );

  if (open) {
    return (
      <>
        <div className="flex h-12 shrink-0 items-center gap-1 border-b border-zinc-100 px-2">
          <button
            type="button"
            onClick={() => {
              setOpenId(null);
              setIconQuery("");
            }}
            title="Zurück zu allen Elementen"
            aria-label="Zurück zu allen Elementen"
            className="flex size-8 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100"
          >
            <ChevronLeft className="size-4" />
          </button>
          <h2 className="text-sm font-semibold text-zinc-900">{open.label}</h2>
        </div>
        {open.fullPanel ? (
          open.render("")
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {open.render(open.id === "icons" ? iconQuery : "")}
          </div>
        )}
      </>
    );
  }

  const searching = normalize(query).length >= 2;
  const found = categories.filter((category) => matches(category.keywords, category.label, query));
  const textFound = matches(TEXT_KEYWORDS, "Text", query);
  const shapesFound = matches(SHAPE_KEYWORDS, "Formen", query);
  const iconsMatched = found.some((category) => category.id === "icons");

  return (
    <>
      <PanelTitle>Elemente</PanelTitle>
      <div className="border-b border-zinc-100 p-3">
        <label className="flex h-8 items-center gap-2 rounded-md border border-zinc-200 px-2 focus-within:border-brand-500">
          <Search className="size-4 shrink-0 text-zinc-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Suchen, z. B. Pfeil, QR, Herz"
            aria-label="Elemente suchen"
            className="w-full bg-transparent text-sm outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {query ? (
            <button
              type="button"
              title="Suche leeren"
              onClick={() => setQuery("")}
              className="rounded p-0.5 text-zinc-400 hover:text-zinc-700"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </label>
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        {searching ? (
          <>
            {textFound ? (
              <div className="space-y-2">
                <SectionLabel>Text</SectionLabel>
                <TextButtons />
              </div>
            ) : null}
            {shapesFound ? (
              <div className="space-y-2">
                <SectionLabel>Formen</SectionLabel>
                <ShapeTiles />
              </div>
            ) : null}
            {found.map((category) => (
              <div key={category.id} className="space-y-2">
                <SectionLabel
                  action={
                    <button
                      type="button"
                      onClick={() => setOpenId(category.id)}
                      className="flex items-center text-[11px] font-medium text-brand-700 hover:underline"
                    >
                      Öffnen <ChevronRight className="size-3" />
                    </button>
                  }
                >
                  {category.label}
                </SectionLabel>
                {category.fullPanel ? (
                  <CategoryCard category={category} onOpen={() => setOpenId(category.id)} />
                ) : (
                  category.render("")
                )}
              </div>
            ))}
            {iconsMatched ? null : (
              <div className="space-y-2">
                <SectionLabel
                  action={
                    <button
                      type="button"
                      onClick={() => {
                        setIconQuery(query.trim());
                        setOpenId("icons");
                      }}
                      className="flex items-center text-[11px] font-medium text-brand-700 hover:underline"
                    >
                      Alle Icons <ChevronRight className="size-3" />
                    </button>
                  }
                >
                  Icons zu „{query.trim()}“
                </SectionLabel>
                <IconSearchPreview query={query.trim()} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="space-y-2">
              <SectionLabel>Text</SectionLabel>
              <TextButtons />
            </div>
            <div className="space-y-2">
              <SectionLabel>Formen</SectionLabel>
              <ShapeTiles />
            </div>
            <div className="space-y-2">
              <SectionLabel>Mehr</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    onOpen={() => setOpenId(category.id)}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
